import { ScrapbookContentType } from '@/types';
import { Link, ResponsiveImage, EntityCardSize, SystemFeaturesContext, UserContext } from '@/components';
import { useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import ImageIcon from '@/assets/imageIcon.svg';
import ScissorIcon from '../../assets/scissors-light.svg';
import ClockIcon from '../../assets/clockLight.svg';
import EyeIcon from '../../assets/eye-light.svg';
import CheckboxCircleIcon from '../../assets/checkboxCircle.svg';
import ReportButton from '../common/ReportButton';
import { useOptimizedPreview } from '../scrapbook/previews/OptimizedPreview';
import { useTranslations } from 'next-intl';
import { SaveToScrapbookButton } from '../scrapbook';
import { ReportCardModel } from './ReportCard';

const ReportCardStylus = ({
  data,
  className,
  size,
  onClick,
  hideTitle,
  hideViewed = false,
  hideTopContent,
  hideSaveToScrapbook,
  ...props
}: ReportCardModel) => {
  const isFullLeyaut = (!data.isLegacy && !data.thumbnailBackgroundColour) || hideTitle || data.hideTitleOnAnchor;
  const { reports: systemFeaturesReports, saveToScrapbook: systemFeaturesSaveToScrapbook } =
    useContext(SystemFeaturesContext);
  const user = useContext(UserContext);
  const { openReportPreview } = useOptimizedPreview();
  const [isVisitedPage, setIsVisitedPage] = useState(false);

  const t = useTranslations();

  useEffect(() => {
    const checkVisitedStatus = () => {
      const visitedIds = localStorage.getItem('visitedPageIds');
      const reportIdStr = data.reportId?.toString();
      // Handle both single ID and comma-separated IDs
      const visitedArray = visitedIds ? visitedIds.split(',') : [];
      const isVisited = visitedArray.includes(reportIdStr);
      setIsVisitedPage(isVisited);
    };

    // Check immediately on mount
    checkVisitedStatus();

    // Listen for localStorage changes across tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'visitedPageIds') {
        checkVisitedStatus();
      }
    };

    // Also listen for custom events (for same-tab updates)
    const handleVisitedUpdate = () => {
      checkVisitedStatus();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('visitedPagesUpdated', handleVisitedUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('visitedPagesUpdated', handleVisitedUpdate);
    };
  }, [data.reportId, hideViewed]);

  const handlePreview = () => {
    if (data.reportId) {
      openReportPreview(data.reportId.toString());
    }
  };

  let imageWidths: { [x: string]: number } = {};
  let imageHeights: { [x: string]: number } = {};

  if (size === EntityCardSize.SMALL) {
    if (!isFullLeyaut) {
      imageWidths = { xs: 261, sm: 261, md: 261, lg: 392, xl: 392 };
      imageHeights = { xs: 146, sm: 146, md: 146, lg: 219, xl: 219 };
    } else {
      imageWidths = { xs: 246, sm: 272, md: 272, lg: 366, xl: 366 };
      imageHeights = { xs: 218, sm: 240, md: 240, lg: 326, xl: 326 };
    }
  } else {
    if (!isFullLeyaut) {
      imageWidths = { xs: 527, sm: 527, md: 527, lg: 589, xl: 589 };
      imageHeights = { xs: 298, sm: 298, md: 298, lg: 329, xl: 329 };
    } else {
      imageWidths = { xs: 550, sm: 550, md: 550, lg: 720, xl: 720 };
      imageHeights = { xs: 490, sm: 490, md: 490, lg: 641, xl: 641 };
    }
  }

  if (data.isLegacy) {
    Object.entries(imageHeights).forEach(([key]) => {
      imageHeights[key] = Math.ceil((imageWidths[key] * 337) / 600);
    });
  }

  const _onClick = (e: React.MouseEvent) => {
    onClick?.(e, data);
  };

  return (
    <div
      className={classNames(
        'report-card-container overflow-hidden',
        size === EntityCardSize.LARGE
          ? 'xxl:h-[568px] xxl:w-[640px] h-[335px] w-[335px] lg:h-[400px] lg:w-[400px] xl:h-[497px] xl:w-[560px]'
          : size === EntityCardSize.SMALL
            ? 'h-[218px] w-[246px] sm:h-[240px] sm:w-[272px] xl:h-[326px] xl:w-[366px]'
            : 'aspect-[456/406] w-full',
        size,
        data.isLoading ? 'loading' : 'loaded',
        className,
      )}
      {...props}
    >
      {data.isLoading ? (
        <div className="flex h-full w-full animate-pulse items-center justify-center bg-gray-300">
          <ImageIcon className="h-10 w-10 text-gray-200 dark:text-gray-600" />
        </div>
      ) : (
        <div className="bg-gray-2 group relative flex h-full flex-col">
          <Link link={data.reportUrl} className="flex h-full flex-col" onClick={_onClick}>
            {!hideTopContent && (
              <div
                className={classNames(
                  'report-card pointer-events-none flex h-full grow flex-col',
                  isFullLeyaut && 'absolute w-full',
                )}
                style={{
                  color: data.anchorTextColour,
                  backgroundColor: data.hideTitleOnAnchor ? undefined : data.thumbnailBackgroundColour,
                }}
              >
                <div
                  className={classNames(
                    'pointer-events-auto flex items-center justify-between p-2 leading-[18px] font-bold lg:p-3 xl:p-4 xl:pb-2',
                    size !== EntityCardSize.LARGE && 'text-xs leading-5',
                  )}
                >
                  <div className="flex">
                    {systemFeaturesReports && <span>{data.format}</span>}
                    {systemFeaturesReports && <span className="mx-1.5 opacity-50">|</span>}
                    <span>{data.directory?.name}</span>
                  </div>
                  <span className="nowrap hidden xl:inline-block">
                    {dayjs(data.publishedDate)?.format('DD MMMM YYYY')}
                  </span>
                  <span className="nowrap xl:hidden">
                    {dayjs(data.publishedDate)?.format(size === EntityCardSize.LARGE ? 'DD MMMM YYYY' : 'YYYY')}
                  </span>
                </div>
                {!isFullLeyaut && (
                  <div className="title xl-pt-0 flex h-full grow items-start p-2 pt-0 lg:p-3 lg:pt-0 xl:p-4 xl:pt-0">
                    <div className="line-clamp-2 text-sm leading-[18px] font-light tracking-[0.1px] sm:text-lg sm:leading-6 md:font-normal xl:text-xl xl:leading-[26px]">
                      {data.title}
                    </div>
                  </div>
                )}
              </div>
            )}
            {data.image && (
              <ResponsiveImage
                image={data.image}
                widths={imageWidths}
                heights={imageHeights}
                cdnProps={{ mode: 'crop' }}
                imgProps={{ className: 'aspect-video' }}
              />
            )}
          </Link>

          {/* Action Buttons - Hover Only */}
          {(user.isAuthenticated || user.isIpBasedIdentity) && (
            <div className="buttons pointer-events-none absolute bottom-0 hidden w-full justify-end opacity-0 transition-opacity duration-500 group-hover:opacity-100 md:flex pointer-coarse:opacity-100">
              <div className="button-list mr-2 mb-2">
                {!hideViewed && isVisitedPage && (
                  <div className="flex justify-end">
                    <ReportButton icon={CheckboxCircleIcon} text={t('general.$viewed')} textClassName="pr-7" />
                  </div>
                )}
                {data.readTime && parseInt(data.readTime) > 0 && (
                  <div className="flex justify-end">
                    <ReportButton icon={ClockIcon} text={t('report.$readTime', { time: data.readTime })} />
                  </div>
                )}
                <div className="flex justify-end">
                  <ReportButton
                    icon={EyeIcon}
                    text={t('general.$preview')}
                    onClick={handlePreview}
                    textClassName="pr-5"
                  />
                </div>
                {!user.isIpBasedIdentity && systemFeaturesSaveToScrapbook && !hideSaveToScrapbook && (
                  <div className="flex justify-end">
                    <SaveToScrapbookButton
                      scrapbookContent={{
                        pageId: data.reportId,
                        pageTitle: data.title,
                        directory: data.directory?.name,
                        readTime: data.readTime,
                        publishedDate: data.publishedDate,
                        pageUrl: data.reportUrl,
                        thumbnailImage: data.image,
                      }}
                      scrapbookContentType={ScrapbookContentType.Article}
                      scrapbookPath={`rprt.${data.reportId}.link.json`}
                      successMessage="Report saved to scrapbook successfully"
                      tooltipProps={{ inactive: true, content: ' ' }}
                      className="cursor-pointer"
                    >
                      <ReportButton icon={ScissorIcon} text={t('general.$save')} textClassName="pr-10" />
                    </SaveToScrapbookButton>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportCardStylus;
